// src/components/CookiePreferencesModal.js
import React, { useState, useEffect } from 'react';
import {CookieIcon, LucchettIcon, MarketIcon, GraficitIcon, ImpostIcon, InfoIcon, DettaglIcon, StemmaIcon, LegalIcon} from '../../svgs/EvironemtIcon';
import axios from 'axios';
import './cookie-preferencesmodal.scss';

// 🔥 Funzione helper per attivare script senza ricaricare
const activateScriptsByConsent = (categories) => {
    console.log('🔄 Attivazione script per categorie:', categories);
    
    // Google Analytics (esempio)
    if (categories.statistics && window.gtag) {
        window.gtag('consent', 'update', {
            analytics_storage: 'granted'
        });
        console.log('✅ Google Analytics attivato');
    }
    
    // Facebook Pixel (esempio)
    if (categories.marketing && window.fbq) {
        window.fbq('consent', 'grant');
        console.log('✅ Facebook Pixel attivato');
    }
    
    // Script differiti con attributo data-cookie-category
    document.querySelectorAll('script[data-cookie-category]').forEach(script => {
        const scriptCategory = script.getAttribute('data-cookie-category');
        if (categories[scriptCategory] && script.getAttribute('data-executed') !== 'true') {
            const newScript = document.createElement('script');
            Array.from(script.attributes).forEach(attr => {
                if (attr.name !== 'data-executed') {
                    newScript.setAttribute(attr.name, attr.value);
                }
            });
            newScript.setAttribute('data-executed', 'true');
            if (script.src) {
                newScript.src = script.src;
            } else {
                newScript.textContent = script.textContent;
            }
            script.parentNode.replaceChild(newScript, script);
            console.log(`✅ Script ${scriptCategory} attivato`);
        }
    });
    
    // Dispatch evento per altri componenti
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
        detail: categories 
    }));
};

// 🔥 Funzione per disattivare script opzionali
const deactivateOptionalScripts = () => {
    document.querySelectorAll('[data-cookie-category="marketing"], [data-cookie-category="statistics"]').forEach(el => {
        el.remove();
    });
    window.dispatchEvent(new CustomEvent('cookieConsentRevoked'));
};

const CookiePreferencesModal = ({ isOpen, onClose, onSave }) => {
    const [preferences, setPreferences] = useState({
        necessary: true,
        preferences: false,
        statistics: false,
        marketing: false
    });
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('preferences');

    useEffect(() => {
        if (isOpen) {
            fetchPreferences();
            fetchPolicy();
        }
    }, [isOpen]);

    // ✅ CORRETTO - axios con .data
    const fetchPreferences = async () => {
        try {
            const response = await axios.get('/api/cookie-consent-status', {
                withCredentials: true
            });
            // ✅ I dati sono già in response.data, NON chiamare .json()
            const data = response.data;
            
            if (data.success && data.data?.categories) {
                setPreferences({
                    necessary: true,
                    preferences: data.data.categories.preferences || false,
                    statistics: data.data.categories.statistics || false,
                    marketing: data.data.categories.marketing || false
                });
            }
        } catch (error) {
            console.error('Errore caricamento preferenze:', error);
        }
    };

    // ✅ CORRETTO - axios con .data
    const fetchPolicy = async () => {
        try {
            const response = await axios.get('/api/cookie-policy', {
                withCredentials: true
            });
            // ✅ I dati sono già in response.data, NON chiamare .json()
            const data = response.data;
            if (data.success) {
                setPolicy(data.data);
            }
        } catch (error) {
            console.error('Errore caricamento policy:', error);
        }
    };

    // 🔥 HANDLE SAVE - SENZA RICARICAMENTO
    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await axios.post('/api/cookie-consent', {
                categories: {
                    preferences: preferences.preferences,
                    statistics: preferences.statistics,
                    marketing: preferences.marketing
                }
            }, {
                withCredentials: true
            });
            
            // ✅ I dati sono già in response.data
            if (response.data.success) {
                if (onSave) onSave();
                onClose();
                
                // 🔥 Attiva script senza ricaricare
                activateScriptsByConsent({
                    preferences: preferences.preferences,
                    statistics: preferences.statistics,
                    marketing: preferences.marketing
                });
                
                // Mostra toast di conferma (opzionale)
                if (window.showToast) {
                    window.showToast('✅ Preferenze cookie salvate', 'success', 3000);
                }
            }
        } catch (error) {
            console.error('Errore salvataggio:', error);
            if (window.showToast) {
                window.showToast('❌ Errore salvataggio preferenze', 'error', 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    // 🔥 HANDLE REVOKE - SENZA RICARICAMENTO
    const handleRevoke = async () => {
        setLoading(true);
        try {
            const response = await axios.post('/api/cookie-consent/revoke', {}, {
                withCredentials: true
            });
            
            // ✅ I dati sono già in response.data
            if (response.data.success) {
                setPreferences({
                    necessary: true,
                    preferences: false,
                    statistics: false,
                    marketing: false
                });
                if (onSave) onSave();
                
                // 🔥 Disattiva script opzionali
                deactivateOptionalScripts();
                
                if (window.showToast) {
                    window.showToast('🔒 Consenso revocato', 'info', 3000);
                }
            }
        } catch (error) {
            console.error('Errore revoca:', error);
            if (window.showToast) {
                window.showToast('❌ Errore revoca consenso', 'error', 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    // 🔥 HANDLE ACCEPT ALL - SENZA RICARICAMENTO
    const handleAcceptAll = async () => {
        setLoading(true);
        try {
            const response = await axios.post('/api/cookie-consent', {
                categories: {
                    preferences: true,
                    statistics: true,
                    marketing: true
                }
            }, {
                withCredentials: true
            });
            
            // ✅ I dati sono già in response.data
            if (response.data.success) {
                if (onSave) onSave();
                onClose();
                
                // 🔥 Attiva tutti gli script
                activateScriptsByConsent({
                    preferences: true,
                    statistics: true,
                    marketing: true
                });
                
                if (window.showToast) {
                    window.showToast('✅ Tutti i cookie attivati', 'success', 3000);
                }
            }
        } catch (error) {
            console.error('Errore accetta tutti:', error);
            if (window.showToast) {
                window.showToast('❌ Errore attivazione cookie', 'error', 3000);
            }
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="cookie-modal-overlay" onClick={onClose}>
            <div className="cookie-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="cookie-modal__header">
                    <div className="cookie-modal__header-icon">
                        <span className="cookie-icon"><CookieIcon stroke={'#edf5e1'} width={28} height={28}/></span>
                    </div>
                    <div className="cookie-modal__header-text">
                        <h2>Gestione Cookie</h2>
                        <p>Personalizza le tue preferenze sui cookie</p>
                    </div>
                    <button className="cookie-modal__close" onClick={onClose}>
                        <span>✕</span>
                    </button>
                </div>

                {/* Tabs */}
                <div className="cookie-modal__tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
                        onClick={() => setActiveTab('preferences')}
                    >
                        <span><ImpostIcon color={'currentColor'} width={18} height={18}/></span> Preferenze
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                        onClick={() => setActiveTab('details')}
                    >
                        <span><DettaglIcon  color={'currentColor'} width={22} height={22}/></span> Dettaglio Cookie
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => setActiveTab('info')}
                    >
                        <span><InfoIcon  color={'currentColor'} width={22} height={22}/></span> Informazioni
                    </button>
                </div>

                {/* Content */}
                <div className="cookie-modal__content">
                    {activeTab === 'preferences' && (
                        <div className="preferences-tab">
                            <p className="info-text">
                                Scegli quali categorie di cookie vuoi abilitare. 
                                I cookie necessari sono sempre attivi per garantire il corretto funzionamento del sito.
                            </p>
                            
                            <div className="cookie-options">
                                {/* Necessary - Sempre attivi */}
                                <div className="cookie-option necessary">
                                    <div className="option-info">
                                        <div className="option-icon"><LucchettIcon color={'#2c3e50'} width={28} height={28}/></div>
                                        <div className="option-text">
                                            <h4>Cookie Necessari</h4>
                                            <p>Essenziali per il funzionamento del sito. Non possono essere disattivati.</p>
                                            <div className="cookie-list">
                                                <span className="cookie-tag">access_token</span>
                                                <span className="cookie-tag">refresh_token</span>
                                                <span className="cookie-tag">session_id</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="option-status">
                                        <span className="always-active-badge">Sempre attivi</span>
                                    </div>
                                </div>

                                {/* Preferences */}
                                <div className="cookie-option">
                                    <div className="option-info">
                                        <div className="option-icon"><ImpostIcon stroke={'#2c3e50'} width={28} height={28}/></div>
                                        <div className="option-text">
                                            <h4>Cookie di Preferenze</h4>
                                            <p>Ricordano le tue scelte come lingua, tema e layout preferiti.</p>
                                            <div className="cookie-list">
                                                <span className="cookie-tag">theme_preference</span>
                                                <span className="cookie-tag">language</span>
                                            </div>
                                        </div>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.preferences}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                preferences: e.target.checked
                                            })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>

                                {/* Statistics */}
                                <div className="cookie-option">
                                    <div className="option-info">
                                        <div className="option-icon"><GraficitIcon color={'#2c3e50'} width={28} height={28}/></div>
                                        <div className="option-text">
                                            <h4>Cookie Statistici</h4>
                                            <p>Ci aiutano a capire come interagisci con il sito per migliorare l'esperienza.</p>
                                            <div className="cookie-list">
                                                <span className="cookie-tag">_ga</span>
                                                <span className="cookie-tag">_gid</span>
                                            </div>
                                        </div>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.statistics}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                statistics: e.target.checked
                                            })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>

                                {/* Marketing */}
                                <div className="cookie-option">
                                    <div className="option-info">
                                        <div className="option-icon"><MarketIcon color={'#2c3e50'} width={28} height={28}/></div>
                                        <div className="option-text">
                                            <h4>Cookie di Marketing</h4>
                                            <p>Utilizzati per mostrarti pubblicità pertinenti basate sui tuoi interessi.</p>
                                            <div className="cookie-list">
                                                <span className="cookie-tag">_fbp</span>
                                            </div>
                                        </div>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.marketing}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                marketing: e.target.checked
                                            })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'details' && policy && (
                        <div className="details-tab">
                            <div className="cookie-table">
                                <div className="table-header">
                                    <span>Nome Cookie</span>
                                    <span>Categoria</span>
                                    <span>Durata</span>
                                    <span>Descrizione</span>
                                </div>
                                {Object.entries(policy.categories).flatMap(cat => 
                                    cat[1]?.cookies?.map(cookieName => {
                                        let cookieDef = null;
                                        if (cookieName === 'access_token') cookieDef = { category: 'necessary', duration: '15 minuti', description: 'Autenticazione utente' };
                                        if (cookieName === 'refresh_token') cookieDef = { category: 'necessary', duration: '7 giorni', description: 'Rinnovo sessione' };
                                        if (cookieName === 'session_id') cookieDef = { category: 'necessary', duration: '30 giorni', description: 'Identificativo sessione' };
                                        if (cookieName === 'theme_preference') cookieDef = { category: 'preferences', duration: '1 anno', description: 'Tema preferito' };
                                        if (cookieName === 'language') cookieDef = { category: 'preferences', duration: '1 anno', description: 'Lingua preferita' };
                                        if (cookieName === '_ga') cookieDef = { category: 'statistics', duration: '2 anni', description: 'Google Analytics' };
                                        if (cookieName === '_gid') cookieDef = { category: 'statistics', duration: '24 ore', description: 'Google Analytics sessione' };
                                        if (cookieName === '_fbp') cookieDef = { category: 'marketing', duration: '90 giorni', description: 'Facebook Pixel' };
                                        
                                        if (!cookieDef) return null;
                                        
                                        return (
                                            <div key={cookieName} className="table-row">
                                                <span className="cookie-name"><code>{cookieName}</code></span>
                                                <span className="cookie-category">
                                                    <span className={`category-badge ${cookieDef.category}`}>
                                                        {cookieDef.category}
                                                    </span>
                                                </span>
                                                <span className="cookie-duration">{cookieDef.duration}</span>
                                                <span className="cookie-description">{cookieDef.description}</span>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'info' && (
                        <div className="info-tab">
                            <div className="info-section">
                                <div className="info-icon"><CookieIcon stroke={'#2c3e50'} width={32} height={32}/></div>
                                <div className="info-text">
                                    <h3>Cosa sono i cookie?</h3>
                                    <p>
                                        I cookie sono piccoli file di testo che vengono salvati sul tuo dispositivo 
                                        quando visiti un sito web. Sono utilizzati per ricordare le tue azioni e preferenze 
                                        nel tempo, in modo da non doverle reinserire ogni volta.
                                    </p>
                                </div>
                            </div>
                            <div className="info-section">
                                <div className="info-icon"><StemmaIcon color={'#2c3e50'} width={48} height={48}/></div>
                                <div className="info-text">
                                    <h3>La tua privacy è importante</h3>
                                    <p>
                                        Puoi gestire le tue preferenze in qualsiasi momento attraverso questo pannello. 
                                        I cookie necessari non possono essere disattivati perché garantiscono 
                                        le funzionalità base del sito.
                                    </p>
                                </div>
                            </div>
                            <div className="info-section">
                                <div className="info-icon"><LegalIcon color={'#2c3e50'} width={48} height={48}/></div>
                                <div className="info-text">
                                    <h3>Base legale</h3>
                                    <p>
                                        Il trattamento dei tuoi dati avviene nel rispetto del GDPR (Regolamento UE 2016/679). 
                                        Per i cookie statistici e di marketing richiediamo il tuo consenso esplicito.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="cookie-modal__footer">
                    <button 
                        className="btn-revoke"
                        onClick={handleRevoke}
                        disabled={loading}
                    >
                        Revoca tutti
                    </button>
                    <div className="footer-buttons">
                        <button 
                            className="btn-accept-all"
                            onClick={handleAcceptAll}
                            disabled={loading}
                        >
                            Accetta tutti
                        </button>
                        <button 
                            className="btn-primary"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading ? 'Salvataggio...' : 'Salva preferenze'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookiePreferencesModal;